import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Megaphone,
  Plus,
  Send,
} from 'lucide-react';
import {
  createAdminAnnouncement,
  fetchAdminAnnouncement,
  fetchAdminAnnouncements,
  patchAdminAnnouncement,
  type AdminAnnouncementRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function priorityLabel(priority: string) {
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

function CreateAnnouncementModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    message: '',
    priority: 'medium',
    actionRoute: '',
    actionLabel: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({ title: '', message: '', priority: 'medium', actionRoute: '', actionLabel: '' });
    setError('');
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createAdminAnnouncement({
        title: form.title.trim(),
        message: form.message.trim(),
        priority: form.priority,
        actionRoute: form.actionRoute.trim() || undefined,
        actionLabel: form.actionLabel.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-announcements'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create announcement');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-announcement-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Broadcast</p>
            <h2 id="create-announcement-title"><Send size={20} /> New announcement</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Title</span>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Message</span>
            <textarea
              required
              rows={4}
              className="wfx-admin-textarea"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            />
          </label>
          <label className="wfx-admin-field">
            <span>Priority</span>
            <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Action route</span>
              <input
                placeholder="/vault"
                value={form.actionRoute}
                onChange={(e) => setForm((f) => ({ ...f, actionRoute: e.target.value }))}
              />
            </label>
            <label className="wfx-admin-field">
              <span>Action label</span>
              <input
                placeholder="Open Vault"
                value={form.actionLabel}
                onChange={(e) => setForm((f) => ({ ...f, actionLabel: e.target.value }))}
              />
            </label>
          </div>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Publishing…' : 'Publish announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AnnouncementDetailCard({
  announcementId,
  onClose,
}: {
  announcementId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-announcement', announcementId],
    queryFn: () => fetchAdminAnnouncement(announcementId!),
    enabled: Boolean(announcementId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminAnnouncementRow>) => patchAdminAnnouncement(announcementId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-announcements'] });
      void qc.invalidateQueries({ queryKey: ['admin-announcement', announcementId] });
    },
  });

  const row = data?.announcement;

  return (
    <AdminDetailCard
      open={Boolean(announcementId)}
      onClose={onClose}
      ariaLabel="Announcement details"
      breadcrumb="Platform announcement"
      title={row?.title ?? 'Loading…'}
      illustration="admin.module.notifications"
      subtitle={
        row ? (
          <>
            <AdminStatusBadge status={row.priority} />
            <span className="wfx-muted wfx-admin-subline">
              {new Date(row.createdAt).toLocaleString()}
            </span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading announcement…" />}
      {isError && <ErrorState message="Could not load announcement." onRetry={() => void refetch()} />}

      {row && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Priority</dt><dd>{priorityLabel(row.priority)}</dd></div>
            <div><dt>Category</dt><dd>{row.categoryId ?? row.type}</dd></div>
            <div><dt>Action</dt><dd>{row.actionLabel ?? '—'}</dd></div>
            <div>
              <dt>Route</dt>
              <dd>
                {row.actionRoute ? (
                  <Link to={row.actionRoute} className="secondary-action compact" target="_blank">
                    {row.actionRoute}
                    <ExternalLink size={12} aria-hidden="true" />
                  </Link>
                ) : '—'}
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Edit announcement</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={row.title}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.title) update.mutate({ title: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Message</span>
                  <textarea
                    rows={5}
                    className="wfx-admin-textarea"
                    defaultValue={row.message}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.message) update.mutate({ message: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Priority</span>
                  <select
                    className="wfx-admin-select"
                    value={row.priority}
                    disabled={update.isPending}
                    onChange={(e) => update.mutate({ priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Call to action</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Action route</span>
                  <input
                    defaultValue={row.actionRoute ?? ''}
                    placeholder="/notifications"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.actionRoute ?? '')) update.mutate({ actionRoute: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Action label</span>
                  <input
                    defaultValue={row.actionLabel ?? ''}
                    placeholder="View details"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.actionLabel ?? '')) update.mutate({ actionLabel: value || null });
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function AnnouncementCard({
  row,
  onSelect,
}: {
  row: AdminAnnouncementRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-announcement-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-announcement-card-head">
        <AdminStatusBadge status={row.priority} />
        <span className="wfx-muted">{new Date(row.createdAt).toLocaleDateString()}</span>
      </div>
      <strong>{row.title}</strong>
      <p className="wfx-muted wfx-admin-blog-card-excerpt">{row.message}</p>
      {row.actionLabel && <span className="chip-pill">{row.actionLabel}</span>}
    </button>
  );
}

export function AdminAnnouncementsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, priorityFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-announcements', debouncedQuery, priorityFilter, page],
    queryFn: () =>
      fetchAdminAnnouncements({
        q: debouncedQuery || undefined,
        priority: priorityFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total', value: stats?.total ?? 0 },
      { label: 'Critical', value: stats?.critical ?? 0 },
      { label: 'High', value: stats?.high ?? 0 },
      { label: 'Medium/Low', value: stats?.medium ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading announcements…" />;
  if (isError) return <ErrorState message="Could not load announcements." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Content / Announcements"
        title="Announcements"
        description="Broadcast platform announcements to users — maintenance, features, and policy updates."
        icon={Megaphone}
        illustration="admin.module.notifications"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New announcement
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <AnnouncementCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>

        <AdminTablePanel
          title="All announcements"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search title or message…"
              />
              <select
                className="wfx-admin-select"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                aria-label="Filter by priority"
              >
                <option value="all">All priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} announcements
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Priority</th><th>Action</th><th>Created</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td>
                    <strong>{row.title}</strong>
                    <span className="wfx-muted wfx-admin-subline">{row.message}</span>
                  </td>
                  <td><AdminStatusBadge status={row.priority} /></td>
                  <td>{row.actionLabel ?? '—'}</td>
                  <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <AdminEmptyState message="No announcements match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateAnnouncementModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <AnnouncementDetailCard announcementId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}
